import React from "react";

/**
 * AppErrorBoundary
 * ----------------
 * Top-level safety net so ANY render crash anywhere in the React tree shows a
 * readable, recoverable error card instead of a fully black/blank screen
 * (which is what happens by default when React unmounts the broken sub-tree).
 *
 * This is critical for the voice-dictation flow: a transcribed string that
 * triggers an unexpected re-render error in any sibling card (project list,
 * notification bell, calendar, ...) used to take down the entire page.
 */
export class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[Novatus] Render-Crash abgefangen:", error, info?.componentStack);
  }

  handleReload = () => {
    // Soft reload first; if state persists across reloads, the user can hit
    // the link below to fully reset the session.
    try { window.location.reload(); } catch { /* noop */ }
  };

  handleHardReset = () => {
    try {
      localStorage.removeItem("novatus_token");
      localStorage.removeItem("novatus_user");
    } catch { /* noop */ }
    try { window.location.href = "/login"; } catch { /* noop */ }
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const msg = (error && (error.message || String(error))) || "Unbekannter Fehler";

    return (
      <div
        data-testid="app-error-boundary"
        className="min-h-screen bg-[#09090B] text-zinc-100 flex items-center justify-center p-5"
      >
        <div className="max-w-md w-full bg-[#18181B] border border-zinc-800 rounded-2xl p-6 shadow-2xl">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-400 text-[12px] font-bold uppercase tracking-wider mb-3">
            ⚠️ Anwendungsfehler
          </div>
          <h1 className="font-display text-[22px] font-bold leading-tight mb-2">
            Etwas ist schief gelaufen
          </h1>
          <p className="text-[14px] text-zinc-400 leading-relaxed mb-4">
            Die Ansicht konnte nicht gerendert werden. Deine Daten sind sicher
            – bitte lade die Seite neu, um weiterzuarbeiten.
          </p>
          <details className="text-[12px] text-zinc-300 mb-5 bg-[#09090B] border border-zinc-800 rounded-lg p-3 font-mono">
            <summary className="cursor-pointer text-zinc-300 mb-1">Technische Details</summary>
            <code className="block whitespace-pre-wrap break-words">{msg}</code>
          </details>
          <div className="flex gap-2">
            <button
              data-testid="app-error-reload-btn"
              onClick={this.handleReload}
              className="flex-1 h-11 rounded-xl bg-[#00E575] text-[#09090B] font-bold text-[14px] hover:bg-[#00C964] active:scale-[0.97] transition"
            >
              Seite neu laden
            </button>
            <button
              data-testid="app-error-logout-btn"
              onClick={this.handleHardReset}
              className="h-11 px-4 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-200 font-semibold text-[13px] active:scale-[0.97] transition"
            >
              Abmelden
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
